/**
 * Report Detail View for Forensic Accountants
 * Shows comprehensive financial data and calculations
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAccountantAuth } from '../contexts/AccountantAuthContext';
import axios from 'axios';
import AccountantLayout from '../components/AccountantLayout';
import CalculationMethodology from '../components/CalculationMethodology';
import VersionHistory from '../components/VersionHistory';
import ESignature from '../components/ESignature';
import ScenarioProjectionsAccountant from '../components/ScenarioProjectionsAccountant';
import ParsedPLDisplay from '../components/ParsedPLDisplay';
import CaseReadinessPanel from '../components/CaseReadinessPanel';
import AIActivityPanel from '../components/AIActivityPanel';
import ExtractedDocumentsPanel from '../components/ExtractedDocumentsPanel';
import { CompactPipelineStatus, useAgentUpdates } from '../components/pipeline';
import { AccountantKeyMetrics, BusinessInfoCard, PayrollReconciliationCard, SourceDocumentsSection, ComparativeAnalysisSection, SensitivityAnalysisSection, QAInsightsSection, BenchmarkPeriodCard, VariableProfitAnalysisCard, GrowthAdjustmentCard, FinalCalculationCard, AdjusterAnalysisSection, AttorneyAnalysisSection, FairSettlementSection, CourtPDFExportSection } from '../components/accountant';
import { ArrowLeft, FileText, AlertTriangle, FileSpreadsheet } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface Report {
  id: string;
  client_name: string;
  claimant_name: string;
  business_address: string;
  business_type: string;
  zone: string;
  event_type?: string;
  event_start_date?: string;
  event_end_date?: string;
  coverage_year?: number;
  year_window?: number[];
  status: string;
  created_at: string;
  completed_at?: string;
  result?: {
    final_claim_amount: number;
    revenue_loss: number;
    causation_passed: boolean;
    step1_variable_profit_loss?: number;
    step2_incremental_loss?: number;
    causation_test_used?: string;
    calculation_detail?: any;
    payroll_reconciliation?: any;
  };
  financial_data?: any;
  // P&L Bypass fields (Phase 1.5)
  has_pl_statement?: boolean;
  parsed_pl_statement?: any;
  pl_parsed_at?: string;
}

// Helper component to show pipeline status with hook
function PipelineStatusLine({ claimId }: { claimId: string }) {
  const { pipeline, loading } = useAgentUpdates(claimId);
  return (
    <div className="mt-2">
      <CompactPipelineStatus pipeline={pipeline} loading={loading} />
    </div>
  );
}

export default function AccountantReportView() {
  const { reportId } = useParams<{ reportId: string }>();
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [permissions, setPermissions] = useState<string[]>([]);
  const [comparativeAnalysis, setComparativeAnalysis] = useState<any>(null);
  const [reviewInsights, setReviewInsights] = useState<any>(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportMessage, setReportMessage] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [profileStatus, setProfileStatus] = useState<any>(null);
  const [justRegenerated, setJustRegenerated] = useState(false);
  const [previousReportTimestamp, setPreviousReportTimestamp] = useState<string | null>(null);
  const [pendingInvitesCount, setPendingInvitesCount] = useState(0);
  
  const { accountant, getAuthHeader } = useAccountantAuth();
  const navigate = useNavigate();

  // In test mode, bypass accountant auth requirement
  const isTestMode = process.env.REACT_APP_DISABLE_AUTH === 'true';
  
  // Memoize the test accountant to prevent re-render loops
  const testAccountant = React.useMemo(() => ({ 
    id: 'test_user_123', 
    email: 'test@claimsync.com', 
    full_name: 'Test Accountant' 
  }), []);
  
  const effectiveAccountant = isTestMode ? testAccountant : accountant;

  useEffect(() => {
    if (!effectiveAccountant) {
      navigate('/accountant/login');
      return;
    }
    
    fetchReport();
    fetchPendingInvitesCount();
  }, [reportId, effectiveAccountant]);

  const fetchPendingInvitesCount = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/invites/pending`,
        { headers: getAuthHeader() }
      );
      setPendingInvitesCount(response.data.length);
    } catch (err: any) {
      console.error('Failed to fetch pending invites count:', err);
    }
  };

  const fetchReport = async () => {
    try {
      // Get the report data using the accountant-specific endpoint
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/view`,
        { headers: getAuthHeader() }
      );
      
      setReport(response.data);
      
      // Get permissions for this report
      const sharesResponse = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/reports/shared`,
        { headers: getAuthHeader() }
      );
      
      const sharedReport = sharesResponse.data.find((r: any) => r.report_id === reportId);
      if (sharedReport) {
        setPermissions(sharedReport.permissions);
      }

      // Fetch AI-powered comparative analysis
      fetchComparativeAnalysis();
      
      // Fetch AI-powered review insights
      fetchReviewInsights();
      
      // Check accountant profile status
      checkProfileStatus();
      
    } catch (err: any) {
      console.error('Failed to fetch report:', err);
      setError(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const fetchComparativeAnalysis = async () => {
    setLoadingAnalysis(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/comparative-analysis`,
        { headers: getAuthHeader() }
      );
      setComparativeAnalysis(response.data);
    } catch (err) {
      console.error('Failed to fetch comparative analysis:', err);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const fetchReviewInsights = async () => {
    setLoadingInsights(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/review-insights`,
        { headers: getAuthHeader() }
      );
      setReviewInsights(response.data);
    } catch (err) {
      console.error('Failed to fetch review insights:', err);
    } finally {
      setLoadingInsights(false);
    }
  };


  const checkProfileStatus = async () => {
    try {
      console.log('Checking profile status...');
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/accountant-profile/check`,
        { headers: getAuthHeader() }
      );
      console.log('Profile status response:', response.data);
      setProfileStatus(response.data);
    } catch (err) {
      console.error('Failed to check profile status:', err);
      // Set default status on error
      setProfileStatus({ has_profile: false, profile_completed: false });
    }
  };

  const handleGenerateExpertReport = async () => {
    // Check if profile is completed
    if (!profileStatus?.profile_completed) {
      if (window.confirm('You need to complete your profile first to generate court-ready expert reports. Complete profile now?')) {
        navigate('/accountant/profile');
      }
      return;
    }

    // Explicit review confirmation
    const confirmed = window.confirm(
      `PROFESSIONAL ATTESTATION CONFIRMATION\n\n` +
      `By clicking OK, you certify that:\n\n` +
      `✓ You have thoroughly reviewed all financial data and source documents\n` +
      `✓ You have verified the BI methodology application and calculations\n` +
      `✓ You have examined the causation analysis and statistical validation\n` +
      `✓ The findings and conclusions are accurate to the best of your professional knowledge\n\n` +
      `This will generate a court-ready expert report with YOUR credentials and professional attestation.\n\n` +
      `Client: ${report.client_name}\n` +
      `Claim Amount: ${formatCurrency(result?.final_claim_amount)}\n\n` +
      `Proceed with expert report generation?`
    );
    
    if (!confirmed) {
      return;
    }

    // Save current report timestamp if it exists
    if (report?.narrative_generated_at) {
      setPreviousReportTimestamp(report.narrative_generated_at);
    }
    
    setGeneratingReport(true);
    setJustRegenerated(false);
    
    // Show progress updates
    const startTime = Date.now();
    const progressInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      console.log(`⏱️ Report generation in progress: ${elapsed}s elapsed...`);
    }, 5000);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/narrator/generate-narrative-accountant/${reportId}`,
        {},
        { 
          headers: getAuthHeader(),
          timeout: 300000  // 5 minute timeout
        }
      );

      clearInterval(progressInterval);
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      
      // Mark as just regenerated
      setJustRegenerated(true);
      
      // Show success notification
      setReportMessage(`Expert report generated successfully! Generated in ${elapsed} seconds. ${response.data.estimated_pages} pages, ${response.data.word_count} words.`);
      
      // Refresh the report to show the narrative
      await fetchReport();
      
      // Scroll to the report section
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Auto-clear the "just regenerated" banner after 10 seconds
      setTimeout(() => {
        setJustRegenerated(false);
      }, 10000);
      
    } catch (error: any) {
      clearInterval(progressInterval);
      console.error('Failed to generate expert report:', error);
      
      let errorMsg = 'Failed to generate expert report';
      if (error.code === 'ECONNABORTED') {
        errorMsg = 'Report generation timed out. Please try again or contact support if the issue persists.';
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      
      setReportError(errorMsg);
    } finally {
      setGeneratingReport(false);
    }
  };


  const formatCurrency = (amount?: number) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value?: number) => {
    if (!value && value !== 0) return 'N/A';
    return `${value.toFixed(2)}%`;
  };

  if (loading) {
    return (
      <AccountantLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mb-4"></div>
            <p className="text-gray-600">Loading report...</p>
          </div>
        </div>
      </AccountantLayout>
    );
  }

  if (error || !report) {
    return (
      <AccountantLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
            <p className="text-gray-600 mb-6">{error || 'Report not found or you do not have access'}</p>
            <Link 
              to="/accountant"
              className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </AccountantLayout>
    );
  }

  const result = report.result;
  const calcDetail = result?.calculation_detail;

  return (
    <AccountantLayout>
      <div className="p-6">
        {/* Page Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{report.client_name}</h1>
            <p className="text-sm text-gray-500">{report.claimant_name} • {report.business_address}</p>
            {/* AI Pipeline Status */}
            <PipelineStatusLine claimId={reportId!} />
          </div>
          
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${
              report.status === 'completed' ? 'bg-green-100 text-green-700' :
              report.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {report.status}
            </span>
            <button
              onClick={() => navigate(`/accountant/reconciliation/${reportId}`)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm font-medium"
            >
              <FileSpreadsheet className="w-4 h-4" />
              P&L Reconciliation
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
        
        {/* Key Metrics */}
        <AccountantKeyMetrics
          finalClaimAmount={result?.final_claim_amount}
          variableProfitLoss={result?.step1_variable_profit_loss}
          incrementalLoss={result?.step2_incremental_loss}
          causationPassed={result?.causation_passed}
          formatCurrency={formatCurrency}
        />

        {/* FORENSIC AI ANALYSIS - New Feature */}
        {report && (
          <AdjusterAnalysisSection
            reportId={reportId!}
            getAuthHeader={getAuthHeader}
            formatCurrency={formatCurrency}
          />
        )}

        {/* ATTORNEY CASE ANALYSIS - New Feature */}
        {report && (
          <AttorneyAnalysisSection
            reportId={reportId!}
            getAuthHeader={getAuthHeader}
            formatCurrency={formatCurrency}
          />
        )}

        {/* FAIR SETTLEMENT CALCULATOR - Monte Carlo Mediation */}
        {report && (
          <FairSettlementSection
            reportId={reportId!}
            getAuthHeader={getAuthHeader}
            formatCurrency={formatCurrency}
          />
        )}

        {/* COURT-READY PDF EXPORT */}
        {report && (
          <CourtPDFExportSection
            reportId={reportId!}
            claimantName={report.claimant_name || report.client_name}
            getAuthHeader={getAuthHeader}
          />
        )}

        {/* Business Information */}
        <BusinessInfoCard
          businessType={report.business_type}
          businessAddress={report.business_address}
          zone={report.zone}
          coverageYear={report.coverage_year}
          eventType={report.event_type}
          eventStartDate={report.event_start_date}
          eventEndDate={report.event_end_date}
        />

        {/* Calculation Breakdown */}
        {calcDetail && (
          <>
            {/* Benchmark Period */}
            {calcDetail.benchmark_period && (
              <BenchmarkPeriodCard
                benchmarkPeriod={calcDetail.benchmark_period}
                formatCurrency={formatCurrency}
              />
            )}

            {/* Variable Profit Analysis */}
            {calcDetail.variable_profit_analysis && (
              <VariableProfitAnalysisCard
                variableProfitAnalysis={calcDetail.variable_profit_analysis}
                benchmarkPeriod={calcDetail.benchmark_period}
                formatCurrency={formatCurrency}
                formatPercent={formatPercent}
              />
            )}

            {/* Growth Adjustment */}
            {calcDetail.growth_adjustment && (
              <GrowthAdjustmentCard
                growthAdjustment={calcDetail.growth_adjustment}
                benchmarkRevenue={calcDetail.benchmark_period?.revenue}
                variableMarginPct={calcDetail.variable_profit_analysis?.variable_margin_pct}
                financialData={report.financial_data}
                formatCurrency={formatCurrency}
                formatPercent={formatPercent}
              />
            )}

            {/* Final Calculation */}
            {calcDetail.final_calculation && (
              <FinalCalculationCard
                finalCalculation={calcDetail.final_calculation}
                formatCurrency={formatCurrency}
              />
            )}
          </>
        )}

        {/* Payroll Reconciliation */}
        {result?.payroll_reconciliation && (
          <PayrollReconciliationCard
            payrollReconciliation={result.payroll_reconciliation}
            reportId={reportId!}
            canComment={permissions.includes('comment')}
            getAuthHeader={getAuthHeader}
            formatCurrency={formatCurrency}
            formatPercent={formatPercent}
          />
        )}


        {/* COMPARATIVE ANALYSIS SECTION */}
        {result && (
          <ComparativeAnalysisSection
            reportId={reportId!}
            businessType={report.business_type}
            comparativeAnalysis={comparativeAnalysis}
            loadingAnalysis={loadingAnalysis}
            canComment={permissions.includes('comment')}
            getAuthHeader={getAuthHeader}
            formatCurrency={formatCurrency}
          />
        )}
        {/* SENSITIVITY ANALYSIS SECTION */}
        {result && (
          <SensitivityAnalysisSection
            finalClaimAmount={result.final_claim_amount}
            formatCurrency={formatCurrency}
          />
        )}

        {/* INTERACTIVE LOSS PROJECTION SCENARIOS - Dynamic What-If Analysis */}
        {report && result && (
          <ScenarioProjectionsAccountant 
            reportId={report.id} 
            baseResult={result}
          />
        )}

        {/* AI ACTIVITY TRAIL */}
        <AIActivityPanel reportId={reportId!} className="mb-6" />

        {/* Q&A BOT INSIGHTS SECTION */}
        {result && (
          <QAInsightsSection
            result={result}
            report={report}
            calcDetail={calcDetail}
            formatCurrency={formatCurrency}
            formatPercent={formatPercent}
          />
        )}


        {/* SOURCE DOCUMENTS SECTION */}
        <SourceDocumentsSection
          reportId={reportId!}
          clientName={report.client_name}
          tax1065Files={report.tax1065Files}
          plFiles={report.plFiles}
          uploadedDocuments={report.uploadedDocuments}
          getAuthHeader={getAuthHeader}
        />

        {/* PARSED P&L STATEMENT SECTION (Phase 1.5) */}
        {report.has_pl_statement && report.parsed_pl_statement && (
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <FileSpreadsheet className="w-6 h-6 mr-2 text-blue-600" />
              Parsed P&L Statement
              <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                Reconstruction Bypassed
              </span>
            </h2>
            <ParsedPLDisplay data={report.parsed_pl_statement} showDetails={true} />
          </div>
        )}

        {/* CASE READINESS & SUPPORTING DOCUMENTS (P0/P2) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Case Readiness Panel */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Case Readiness</h2>
            <CaseReadinessPanel caseId={reportId || ''} compact={false} showRefresh={true} />
          </div>

          {/* Extracted Supporting Documents */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <ExtractedDocumentsPanel caseId={reportId || ''} />
          </div>
        </div>

        {/* EXPENSE CLASSIFICATION DETAIL SECTION */}
        {calcDetail?.variable_profit_analysis && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="text-2xl mr-2">🔖</span>
              Expense Classification Methodology
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Detailed breakdown of how expenses were categorized as Variable vs. Fixed using BI standards. 
              This classification is critical for variable profit calculation.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Variable Expenses */}
              <div className="bg-green-50 border-l-4 border-green-600 rounded-lg p-4">
                <h3 className="font-semibold text-green-900 mb-3 flex items-center">
                  <span className="mr-2">📉</span>
                  Variable Expenses (Continuing)
                </h3>
                <p className="text-xs text-green-700 mb-3">
                  Expenses that vary directly with revenue and would not have occurred without sales
                </p>
                <div className="space-y-2">
                  <button
                    onClick={() => navigate(`/accountant/reconciliation/${reportId}?section=${encodeURIComponent('COST OF GOODS SOLD')}&line_item=${encodeURIComponent('Product Costs')}`)}
                    className="flex justify-between items-center py-2 border-b border-green-200 w-full text-left hover:bg-green-100 rounded px-2 transition-colors cursor-pointer group"
                  >
                    <span className="text-sm text-green-800 group-hover:text-green-900 font-medium">Cost of Goods Sold (COGS)</span>
                    <span className="text-sm font-semibold text-green-900 flex items-center gap-2">✓ Variable <span className="text-green-600 opacity-0 group-hover:opacity-100 text-xs">→</span></span>
                  </button>
                  <button
                    onClick={() => navigate(`/accountant/reconciliation/${reportId}?section=${encodeURIComponent('COST OF GOODS SOLD')}&line_item=${encodeURIComponent('Product Costs')}`)}
                    className="flex justify-between items-center py-2 border-b border-green-200 w-full text-left hover:bg-green-100 rounded px-2 transition-colors cursor-pointer group"
                  >
                    <span className="text-sm text-green-800 group-hover:text-green-900 font-medium">Direct Materials</span>
                    <span className="text-sm font-semibold text-green-900 flex items-center gap-2">✓ Variable <span className="text-green-600 opacity-0 group-hover:opacity-100 text-xs">→</span></span>
                  </button>
                  <button
                    onClick={() => navigate(`/accountant/reconciliation/${reportId}?section=${encodeURIComponent('OPERATING EXPENSES')}&line_item=${encodeURIComponent('Commissions')}`)}
                    className="flex justify-between items-center py-2 border-b border-green-200 w-full text-left hover:bg-green-100 rounded px-2 transition-colors cursor-pointer group"
                  >
                    <span className="text-sm text-green-800 group-hover:text-green-900 font-medium">Sales Commissions</span>
                    <span className="text-sm font-semibold text-green-900 flex items-center gap-2">✓ Variable <span className="text-green-600 opacity-0 group-hover:opacity-100 text-xs">→</span></span>
                  </button>
                  <button
                    onClick={() => navigate(`/accountant/reconciliation/${reportId}?section=${encodeURIComponent('OPERATING EXPENSES')}&line_item=${encodeURIComponent('Credit Card Processing Fees')}`)}
                    className="flex justify-between items-center py-2 border-b border-green-200 w-full text-left hover:bg-green-100 rounded px-2 transition-colors cursor-pointer group"
                  >
                    <span className="text-sm text-green-800 group-hover:text-green-900 font-medium">Credit Card Fees</span>
                    <span className="text-sm font-semibold text-green-900 flex items-center gap-2">✓ Variable <span className="text-green-600 opacity-0 group-hover:opacity-100 text-xs">→</span></span>
                  </button>
                  <button
                    onClick={() => navigate(`/accountant/reconciliation/${reportId}?section=${encodeURIComponent('COST OF GOODS SOLD')}&line_item=${encodeURIComponent('Other COGS')}`)}
                    className="flex justify-between items-center py-2 w-full text-left hover:bg-green-100 rounded px-2 transition-colors cursor-pointer group"
                  >
                    <span className="text-sm text-green-800 group-hover:text-green-900 font-medium">Shipping & Delivery</span>
                    <span className="text-sm font-semibold text-green-900 flex items-center gap-2">✓ Variable <span className="text-green-600 opacity-0 group-hover:opacity-100 text-xs">→</span></span>
                  </button>
                </div>
                <div className="mt-4 pt-3 border-t-2 border-green-300">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-green-900">Total Variable</span>
                    <span className="font-bold text-lg text-green-900">
                      {formatCurrency((calcDetail.benchmark_period?.revenue || 0) * (1 - (calcDetail.variable_profit_analysis.variable_margin_pct / 100)))}
                    </span>
                  </div>
                  <p className="text-xs text-green-700 mt-1">
                    {(100 - calcDetail.variable_profit_analysis.variable_margin_pct).toFixed(2)}% of revenue
                  </p>
                </div>
              </div>

              {/* Fixed Expenses */}
              <div className="bg-blue-50 border-l-4 border-blue-600 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-3 flex items-center">
                  <span className="mr-2">📌</span>
                  Fixed Expenses (Non-Continuing)
                </h3>
                <p className="text-xs text-blue-700 mb-3">
                  Expenses that continue regardless of revenue and must be paid during interruption
                </p>
                <div className="space-y-2">
                  <button
                    onClick={() => navigate(`/accountant/reconciliation/${reportId}?section=${encodeURIComponent('OPERATING EXPENSES')}&line_item=${encodeURIComponent('Other Operating Expenses')}`)}
                    className="flex justify-between items-center py-2 border-b border-blue-200 w-full text-left hover:bg-blue-100 rounded px-2 transition-colors cursor-pointer group"
                  >
                    <span className="text-sm text-blue-800 group-hover:text-blue-900 font-medium">Rent / Mortgage</span>
                    <span className="text-sm font-semibold text-blue-900 flex items-center gap-2">✓ Fixed <span className="text-blue-600 opacity-0 group-hover:opacity-100 text-xs">→</span></span>
                  </button>
                  <button
                    onClick={() => navigate(`/accountant/reconciliation/${reportId}?section=${encodeURIComponent('OPERATING EXPENSES')}&line_item=${encodeURIComponent('Employee Wages & Salaries')}`)}
                    className="flex justify-between items-center py-2 border-b border-blue-200 w-full text-left hover:bg-blue-100 rounded px-2 transition-colors cursor-pointer group"
                  >
                    <span className="text-sm text-blue-800 group-hover:text-blue-900 font-medium">Salaries (non-commission)</span>
                    <span className="text-sm font-semibold text-blue-900 flex items-center gap-2">✓ Fixed <span className="text-blue-600 opacity-0 group-hover:opacity-100 text-xs">→</span></span>
                  </button>
                  <button
                    onClick={() => navigate(`/accountant/reconciliation/${reportId}?section=${encodeURIComponent('OPERATING EXPENSES')}&line_item=${encodeURIComponent('Insurance')}`)}
                    className="flex justify-between items-center py-2 border-b border-blue-200 w-full text-left hover:bg-blue-100 rounded px-2 transition-colors cursor-pointer group"
                  >
                    <span className="text-sm text-blue-800 group-hover:text-blue-900 font-medium">Insurance Premiums</span>
                    <span className="text-sm font-semibold text-blue-900 flex items-center gap-2">✓ Fixed <span className="text-blue-600 opacity-0 group-hover:opacity-100 text-xs">→</span></span>
                  </button>
                  <button
                    onClick={() => navigate(`/accountant/reconciliation/${reportId}?section=${encodeURIComponent('OPERATING EXPENSES')}&line_item=${encodeURIComponent('Utilities')}`)}
                    className="flex justify-between items-center py-2 border-b border-blue-200 w-full text-left hover:bg-blue-100 rounded px-2 transition-colors cursor-pointer group"
                  >
                    <span className="text-sm text-blue-800 group-hover:text-blue-900 font-medium">Utilities</span>
                    <span className="text-sm font-semibold text-blue-900 flex items-center gap-2">✓ Fixed <span className="text-blue-600 opacity-0 group-hover:opacity-100 text-xs">→</span></span>
                  </button>
                  <button
                    onClick={() => navigate(`/accountant/reconciliation/${reportId}?section=${encodeURIComponent('OPERATING EXPENSES')}&line_item=${encodeURIComponent('Depreciation')}`)}
                    className="flex justify-between items-center py-2 w-full text-left hover:bg-blue-100 rounded px-2 transition-colors cursor-pointer group"
                  >
                    <span className="text-sm text-blue-800 group-hover:text-blue-900 font-medium">Depreciation</span>
                    <span className="text-sm font-semibold text-blue-900 flex items-center gap-2">✓ Fixed <span className="text-blue-600 opacity-0 group-hover:opacity-100 text-xs">→</span></span>
                  </button>
                </div>
                <div className="mt-4 pt-3 border-t-2 border-blue-300">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-blue-900">Excluded from Claim</span>
                    <span className="font-bold text-lg text-blue-900">Not Covered</span>
                  </div>
                  <p className="text-xs text-blue-700 mt-1">
                    These expenses continue during interruption
                  </p>
                </div>
              </div>
            </div>

            {/* Variable Margin Summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-900 mb-1">📊 Calculated Variable Margin: {formatPercent(calcDetail.variable_profit_analysis.variable_margin_pct)}</p>
                  <p className="text-sm text-blue-800">
                    This margin represents the portion of revenue available to cover fixed expenses and profit. 
                    For {report.business_type || 'this business'}, a margin of {formatPercent(calcDetail.variable_profit_analysis.variable_margin_pct)} means 
                    that for every dollar of revenue, {formatCurrency((calcDetail.variable_profit_analysis.variable_margin_pct / 100))} 
                    is retained after variable costs. This high margin is typical for businesses with low variable costs 
                    (e.g., churches, professional services, real estate).
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <span className="font-semibold">For Legal Defense:</span> The expense classification follows BI methodology 
                and insurance industry standards. Variable expenses total {formatCurrency((calcDetail.benchmark_period?.revenue || 0) * (1 - (calcDetail.variable_profit_analysis.variable_margin_pct / 100)))}, 
                representing {(100 - calcDetail.variable_profit_analysis.variable_margin_pct).toFixed(2)}% of benchmark revenue. 
                All classifications are supported by P&L analysis and industry benchmarks.
              </p>
            </div>
          </div>
        )}

        {/* MONTHLY DATA TABLES SECTION */}
        {(() => {
          // Transform data_YYYY format to yearly_data format for display
          const yearlyData: any = {};
          if (report.financial_data) {
            Object.keys(report.financial_data).forEach(key => {
              if (key.startsWith('data_')) {
                const year = key.replace('data_', '');
                yearlyData[year] = report.financial_data[key];
              }
            });
          }
          
          return Object.keys(yearlyData).length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-2xl mr-2">📊</span>
                Monthly Financial Data - Raw Numbers
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                Complete monthly breakdown of revenue, expenses, and payroll. This is the source data used in all calculations above.
              </p>

              {/* Revenue Table */}
              <div className="mb-8 overflow-x-auto">
                <h3 className="font-semibold text-gray-900 mb-3">Monthly Revenue Breakdown</h3>
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Year</th>
                      {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(month => (
                        <th key={month} className="px-3 py-2 text-right font-semibold text-gray-700">{month}</th>
                      ))}
                      <th className="px-3 py-2 text-right font-semibold text-gray-700 bg-blue-100">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(yearlyData)
                      .sort(([yearA], [yearB]) => parseInt(yearA) - parseInt(yearB))
                      .map(([year, months]: [string, any]) => {
                        const yearTotal = months.reduce((sum: number, m: any) => sum + (m.revenue || 0), 0);
                        const isCoverageYear = parseInt(year) === report.coverage_year;
                        const isBenchmarkYear = calcDetail?.benchmark_period?.start?.includes(year);
                        
                        return (
                          <tr key={year} className={`border-b ${isCoverageYear ? 'bg-red-50' : isBenchmarkYear ? 'bg-green-50' : ''}`}>
                            <td className="px-3 py-2 font-semibold text-gray-900">
                              {year}
                              {isCoverageYear && <span className="ml-2 text-xs text-red-600">◆ Coverage</span>}
                              {isBenchmarkYear && <span className="ml-2 text-xs text-green-600">* Benchmark</span>}
                            </td>
                            {months.map((monthData: any, idx: number) => (
                              <td key={idx} className="px-3 py-2 text-right font-mono text-gray-700">
                                {formatCurrency(monthData.revenue || 0).replace('$', '').replace(',000', 'K')}
                              </td>
                            ))}
                            <td className="px-3 py-2 text-right font-mono font-bold text-gray-900 bg-blue-50">
                              {formatCurrency(yearTotal)}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                <div className="mt-2 flex gap-4 text-xs">
                  <span className="text-green-600">* Benchmark Period months used in calculation</span>
                  <span className="text-red-600">◆ Coverage Period (interruption)</span>
                </div>
              </div>

              {/* Expense Summary Table */}
              <div className="overflow-x-auto">
                <h3 className="font-semibold text-gray-900 mb-3">Annual Expense Summary</h3>
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Year</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700">Total Revenue</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700">Variable Expenses</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700">Fixed Expenses</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700">Total Payroll</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700 bg-green-100">Variable Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(yearlyData)
                      .sort(([yearA], [yearB]) => parseInt(yearA) - parseInt(yearB))
                      .map(([year, months]: [string, any]) => {
                        const yearRevenue = months.reduce((sum: number, m: any) => sum + (m.revenue || 0), 0);
                        // Calculate payroll from either payroll.total or total_payroll
                        const yearPayroll = months.reduce((sum: number, m: any) => {
                          if (m.payroll && typeof m.payroll === 'object') {
                            return sum + (m.payroll.total || 0);
                          }
                          return sum + (m.total_payroll || 0);
                        }, 0);
                        // Calculate officer compensation (fixed salary component)
                        const yearOfficerComp = months.reduce((sum: number, m: any) => {
                          if (m.payroll && typeof m.payroll === 'object') {
                            return sum + (m.payroll.officer_compensation || 0);
                          }
                          return sum + (m.officer_compensation || 0);
                        }, 0);
                        // Calculate fixed expenses from expense breakdown (Rent, Utilities, etc.)
                        const yearFixedExpenses = months.reduce((sum: number, m: any) => {
                          if (m.expenses && typeof m.expenses === 'object') {
                            const rent = m.expenses['Rent'] || m.expenses['rent'] || 0;
                            const utilities = m.expenses['Utilities'] || m.expenses['utilities'] || 0;
                            const insurance = m.expenses['Insurance'] || m.expenses['insurance'] || 0;
                            return sum + rent + utilities + insurance;
                          }
                          return sum;
                        }, 0);
                        const variableExpenses = yearRevenue * (1 - (calcDetail?.variable_profit_analysis?.variable_margin_pct || 0) / 100);
                        const variableProfit = yearRevenue - variableExpenses;
                        
                        return (
                          <tr key={year} className="border-b hover:bg-gray-50">
                            <td className="px-3 py-2 font-semibold text-gray-900">{year}</td>
                            <td className="px-3 py-2 text-right font-mono text-gray-700">{formatCurrency(yearRevenue)}</td>
                            <td className="px-3 py-2 text-right font-mono text-gray-700">{formatCurrency(variableExpenses)}</td>
                            <td className="px-3 py-2 text-right font-mono text-gray-700">
                              {yearFixedExpenses > 0 ? formatCurrency(yearFixedExpenses) : yearOfficerComp > 0 ? formatCurrency(yearOfficerComp) : '-'}
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-gray-700">{formatCurrency(yearPayroll)}</td>
                            <td className="px-3 py-2 text-right font-mono font-bold text-green-700 bg-green-50">{formatCurrency(variableProfit)}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* DATA TRAIL SECTION */}
        {result && calcDetail && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="text-2xl mr-2">🔍</span>
              Data Trail - Source to Result
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Visual flowchart showing how source documents became final claim amount. Critical for court testimony and audit verification.
            </p>

            <div className="space-y-4">
              {/* Step 1 */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 font-bold text-blue-700">1</div>
                <div className="flex-1 bg-blue-50 border-l-4 border-blue-600 rounded-lg p-4">
                  <p className="font-semibold text-blue-900 mb-2">SOURCE: Financial Documents</p>
                  <p className="text-sm text-blue-800">
                    {report.plFiles?.length || 0} P&L statements, {report.tax1065Files?.length || 0} tax returns uploaded and processed
                  </p>
                </div>
              </div>

              <div className="ml-6 text-gray-400">↓</div>

              {/* Step 2 */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 font-bold text-green-700">2</div>
                <div className="flex-1 bg-green-50 border-l-4 border-green-600 rounded-lg p-4">
                  <p className="font-semibold text-green-900 mb-2">EXTRACTED: Monthly Data</p>
                  <p className="text-sm text-green-800">
                    {(() => {
                      let yearCount = 0;
                      if (report.financial_data) {
                        yearCount = Object.keys(report.financial_data).filter(k => k.startsWith('data_')).length;
                        if (report.financial_data.yearly_data) {
                          yearCount = Math.max(yearCount, Object.keys(report.financial_data.yearly_data).length);
                        }
                      }
                      return yearCount;
                    })()} years of monthly revenue/expense data extracted via OCR
                  </p>
                </div>
              </div>

              <div className="ml-6 text-gray-400">↓</div>

              {/* Step 3 */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 font-bold text-blue-700">3</div>
                <div className="flex-1 bg-blue-50 border-l-4 border-blue-600 rounded-lg p-4">
                  <p className="font-semibold text-blue-900 mb-2">CALCULATED: Benchmark Period</p>
                  <p className="text-sm text-blue-800 font-mono">
                    {calcDetail.benchmark_period?.start} to {calcDetail.benchmark_period?.end}: Revenue = {formatCurrency(calcDetail.benchmark_period?.revenue)}
                  </p>
                </div>
              </div>

              <div className="ml-6 text-gray-400">↓</div>

              {/* Step 4 */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0 font-bold text-yellow-700">4</div>
                <div className="flex-1 bg-yellow-50 border-l-4 border-yellow-600 rounded-lg p-4">
                  <p className="font-semibold text-yellow-900 mb-2">APPLIED: Variable Margin</p>
                  <p className="text-sm text-yellow-800 font-mono">
                    {formatCurrency(calcDetail.benchmark_period?.revenue)} × {formatPercent(calcDetail.variable_profit_analysis?.variable_margin_pct)} = {formatCurrency(calcDetail.variable_profit_analysis?.benchmark_vp)}
                  </p>
                </div>
              </div>

              <div className="ml-6 text-gray-400">↓</div>

              {/* Step 5 */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 font-bold text-red-700">5</div>
                <div className="flex-1 bg-red-50 border-l-4 border-red-600 rounded-lg p-4">
                  <p className="font-semibold text-red-900 mb-2">CALCULATED: Loss</p>
                  <p className="text-sm text-red-800 font-mono">
                    Benchmark VP - Coverage VP = {formatCurrency(calcDetail.variable_profit_analysis?.benchmark_vp)} - {formatCurrency(calcDetail.variable_profit_analysis?.coverage_vp)} = {formatCurrency(calcDetail.variable_profit_analysis?.vp_loss)}
                  </p>
                </div>
              </div>

              <div className="ml-6 text-gray-400">↓</div>

              {/* Step 6 */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 font-bold text-blue-700">6</div>
                <div className="flex-1 bg-blue-50 border-l-4 border-blue-600 rounded-lg p-4">
                  <p className="font-semibold text-blue-900 mb-2">ADJUSTED: Growth Trend</p>
                  <p className="text-sm text-blue-800 font-mono">
                    {formatCurrency(calcDetail.variable_profit_analysis?.vp_loss)} + {formatCurrency(calcDetail.growth_adjustment?.incremental_lost_profit)} = {formatCurrency(calcDetail.final_calculation?.total_before_threshold)}
                  </p>
                </div>
              </div>

              <div className="ml-6 text-gray-400">↓</div>

              {/* Final Result */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center flex-shrink-0 font-bold text-white">✓</div>
                <div className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg p-6 shadow-lg">
                  <p className="font-bold text-xl mb-2">FINAL CLAIM AMOUNT</p>
                  <p className="text-4xl font-bold">{formatCurrency(result.final_claim_amount)}</p>
                  <p className="text-sm mt-2 opacity-90">Ready for litigation/settlement</p>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">For Court Testimony:</span> "I reviewed {report.plFiles?.length || 0} P&L statements and {report.tax1065Files?.length || 0} tax returns, 
                extracted {(() => {
                  let yearCount = 0;
                  if (report.financial_data) {
                    yearCount = Object.keys(report.financial_data).filter(k => k.startsWith('data_')).length;
                    if (report.financial_data.yearly_data) {
                      yearCount = Math.max(yearCount, Object.keys(report.financial_data.yearly_data).length);
                    }
                  }
                  return yearCount;
                })()} years of monthly data, applied BI methodology, 
                and calculated a final claim amount of {formatCurrency(result.final_claim_amount)}."
              </p>
            </div>
          </div>
        )}

        {/* AUDIT TRAIL NOTES SECTION */}
        {/* Report Metadata */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="text-2xl mr-2">ℹ️</span>
            Report Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Report ID</p>
              <p className="font-mono text-gray-900">{report.id}</p>
            </div>
            <div>
              <p className="text-gray-600">Status</p>
              <p className="font-semibold text-gray-900 capitalize">{report.status}</p>
            </div>
            <div>
              <p className="text-gray-600">Created</p>
              <p className="text-gray-900">{new Date(report.created_at).toLocaleString()}</p>
            </div>
            {report.completed_at && (
              <div>
                <p className="text-gray-600">Completed</p>
                <p className="text-gray-900">{new Date(report.completed_at).toLocaleString()}</p>
              </div>
            )}
            {result?.causation_test_used && (
              <div>
                <p className="text-gray-600">Causation Test</p>
                <p className="text-gray-900">{result.causation_test_used}</p>
              </div>
            )}
          </div>
        </div>

        {/* EXPERT REPORT GENERATION - Bottom of Page After Review */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl shadow-lg border-2 border-green-300 p-8 mt-8">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <svg className="w-12 h-12 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              {/* Show existing narrative if it exists */}
              {report.narrative && report.narrative.word_count > 0 ? (
                <div className="relative">
                  {/* NEW REPORT BANNER */}
                  {justRegenerated && (
                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg p-4 mb-4 animate-pulse">
                      <div className="flex items-center gap-3">
                        <svg className="w-8 h-8 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                          <p className="font-bold text-lg">✨ NEW REPORT GENERATED!</p>
                          <p className="text-sm text-green-100">
                            Generated just now • This is the latest version with your credentials
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <h2 className="text-2xl font-bold text-green-900 mb-2 flex items-center gap-2">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Court-Ready Expert Report {justRegenerated ? '(NEW)' : 'Generated'}
                  </h2>
                  <div className="bg-white rounded-lg border border-green-300 p-4 mb-4">
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-gray-600">Pages</p>
                        <p className="text-2xl font-bold text-green-900">{report.narrative.estimated_pages}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Words</p>
                        <p className="text-2xl font-bold text-green-900">{report.narrative.word_count?.toLocaleString()}</p>
                      </div>
                    </div>
                    {report.narrative.generated_by_accountant && (
                      <div className="border-t pt-3">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">Generated by:</span> {report.narrative.generated_by_accountant}
                        </p>
                        {report.narrative.accountant_credentials && (
                          <p className="text-sm text-gray-600">
                            <span className="font-semibold">Credentials:</span> {report.narrative.accountant_credentials}
                          </p>
                        )}
                        <div className={`mt-2 p-2 rounded ${justRegenerated ? 'bg-green-100 border border-green-300' : 'bg-gray-50'}`}>
                          <p className={`text-sm ${justRegenerated ? 'text-green-900 font-semibold' : 'text-gray-700'}`}>
                            <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            Generated: {new Date(report.narrative_generated_at || '').toLocaleString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                            {justRegenerated && <span className="ml-2 text-xs bg-green-600 text-white px-2 py-0.5 rounded">LATEST</span>}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => window.open(`/accountant/narrator/${reportId}`, '_blank')}
                      disabled={generatingReport}
                      className={`flex-1 px-6 py-3 rounded-lg transition-all font-bold flex items-center justify-center gap-2 ${
                        generatingReport
                          ? 'bg-gray-400 text-gray-700 cursor-not-allowed opacity-50'
                          : justRegenerated 
                          ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white animate-pulse'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      {generatingReport ? '⏳ Old Report (Regenerating...)' : justRegenerated ? '✨ View NEW Report' : 'View Full Report'}
                    </button>
                    
                    <button
                      onClick={() => {
                        if (window.confirm('Regenerate the expert report? This will replace the existing report.')) {
                          handleGenerateExpertReport();
                        }
                      }}
                      disabled={generatingReport}
                      className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-all font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Regenerate
                    </button>
                  </div>
                  
                  <p className="text-xs text-gray-600 mt-3 text-center">
                    💾 Report is permanently saved - you can access it anytime without regenerating
                  </p>
                </div>
              ) : (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Generate Court-Ready Expert Report
                  </h2>
              <p className="text-gray-700 mb-4">
                After thoroughly reviewing all calculations, data, and methodology above, you can generate a comprehensive 
                expert report with your professional attestation. This report will include your credentials and signature, 
                making it ready for court proceedings.
              </p>
              
              <div className="bg-white rounded-lg border border-green-200 p-4 mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">✓ Review Checklist:</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Verified all source documents and financial data</li>
                  <li>• Reviewed BI methodology application and calculations</li>
                  <li>• Confirmed causation analysis and statistical validation</li>
                  <li>• Examined comparative analysis and industry benchmarks</li>
                  <li>• Assessed assumptions and limitations</li>
                  <li>• Ready to provide professional attestation</li>
                </ul>
              </div>

              {/* Profile Status Indicator */}
              <div className={`border rounded-lg p-4 mb-4 ${
                profileStatus?.profile_completed 
                  ? 'bg-green-50 border-green-300' 
                  : 'bg-yellow-50 border-yellow-300'
              }`}>
                {profileStatus?.profile_completed ? (
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm text-green-800 font-medium">
                        ✓ Profile Complete - Ready to Generate Expert Reports
                      </p>
                      <p className="text-xs text-green-700 mt-1">
                        Your credentials will be included in the report attestation.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-yellow-800 font-medium">
                      ⚠️ Complete your profile first to generate expert reports with your credentials.
                    </p>
                    <button
                      onClick={() => navigate('/accountant/profile')}
                      className="mt-2 text-sm text-yellow-900 underline hover:text-yellow-700"
                    >
                      Complete Profile Now →
                    </button>
                  </div>
                )}
              </div>

              <button
                onClick={handleGenerateExpertReport}
                disabled={generatingReport || !profileStatus?.profile_completed}
                className="w-full px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all font-bold text-lg flex items-center justify-center gap-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {generatingReport ? (
                  <>
                    <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating Court-Ready Expert Report...
                  </>
                ) : (
                  <>
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    I Have Reviewed This Case - Generate Expert Report
                  </>
                )}
              </button>

              {profileStatus?.profile_completed && (
                <p className="text-xs text-gray-600 mt-3 text-center">
                  Report will be generated with credentials: <span className="font-semibold">{accountant?.email}</span>
                </p>
              )}
              
              {/* Loading Modal Overlay */}
              {generatingReport && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
                  <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 text-center">
                    <div className="mb-6">
                      <svg className="animate-spin h-16 w-16 mx-auto text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      Generating Expert Report
                    </h3>
                    
                    <p className="text-gray-600 mb-4">
                      AI is analyzing the case and generating a professional report with your credentials...
                    </p>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-blue-800">
                        <strong>⏱️ Estimated Time:</strong> 2-3 minutes
                      </p>
                      <p className="text-xs text-blue-700 mt-2">
                        Generating 5 sections with simplified, professional language
                      </p>
                    </div>
                    
                    <div className="space-y-2 text-left text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span>Analyzing financial data...</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                        <span>Generating sections...</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                        <span>Including your professional credentials...</span>
                      </div>
                    </div>
                    
                    <p className="text-xs text-gray-500 mt-6">
                      Please wait... Do not close this window
                    </p>
                  </div>
                </div>
              )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Collaboration Tools Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Collaboration Tools</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Version History */}
            <VersionHistory
              reportId={reportId!}
              getAuthHeader={getAuthHeader}
            />
            
            {/* E-Signature */}
            <ESignature
              reportId={reportId!}
              getAuthHeader={getAuthHeader}
              canSign={permissions.includes('sign')}
            />
          </div>
        </div>
      </div>
      </div>
    </AccountantLayout>
  );
}
